import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { SeverityLevel } from '@sentry/core';

export const useSentry = () => {
  const captureError = useCallback((error: Error, context: { tags?: Record<string, string>; extra?: Record<string, any> } = {}) => {
    return Sentry.captureException(error, {
      tags: {
        errorBoundary: 'manual',
        ...context?.tags
      },
      extra: {
        timestamp: Date.now(),
        ...context?.extra
      }
    });
  }, []);

  const captureMessage = useCallback((message: string, level: SeverityLevel = 'info', context: { tags?: Record<string, string>; extra?: Record<string, any> } = {}) => {
    return Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra
    });
  }, []);

  const addBreadcrumb = useCallback(
    (category: string, message: string, level: SeverityLevel = 'info', data: Record<string, any> = {}) => {
      Sentry.addBreadcrumb({
        category,
        message,
        level,
        data: {
          timestamp: new Date().toISOString(),
          ...data
        }
      });
    }, 
  []);

  const setUser = useCallback((user: { id: string, email?: string, username?: string }) => {
    if (!user.id) return;
    
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      customData: {
        lastLogin: new Date().toISOString()
      }
    });
  }, []);

  const clearUser = useCallback(() => {
    Sentry.setUser(null);
  }, []);

  const captureEvent = useCallback((event: Sentry.Event) => {
    Sentry.captureEvent(event);
  }, []);

  return {
    captureError,
    captureMessage,
    addBreadcrumb,
    setUser,
    clearUser,
    captureEvent,
  };
};
